// frontend/src/components/Provider/ProviderForm.tsx
// UNIFIED: Mobile-first form component (replaces modal)

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Loader2, AlertCircle, User, Mail, Phone, Building, MapPin } from 'lucide-react';
import { Provider, ProviderFormData } from '@/lib/api/providerService';

interface ProviderFormProps {
  provider?: Provider | null;
  isLoading?: boolean;
  onSubmit: (data: ProviderFormData) => Promise<void>;
  onCancel: () => void;
  errors?: Record<string, string>;
}

const ProviderForm: React.FC<ProviderFormProps> = ({
  provider,
  isLoading = false,
  onSubmit,
  onCancel,
  errors = {}
}) => {
  const [formData, setFormData] = useState<ProviderFormData>({
    business_name: '',
    business_type: '',
    business_email: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    website: '',
    address: '',
    city: '',
    state: '',
    years_experience: 0,
    is_verified: false,
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load existing data when editing
  useEffect(() => {
    if (provider) {
      setFormData({
        business_name: provider.business_name || '',
        business_type: provider.business_type || '',
        business_email: provider.email || '',
        contact_person: provider.user_name || '',
        contact_phone: provider.phone || '',
        contact_email: provider.email || '',
        description: provider.description || '',
        website: provider.website || '',
        address: provider.address || '',
        city: provider.city || '',
        state: provider.state || '',
        years_experience: provider.years_experience || 0,
        is_verified: provider.is_verified || false,
        is_active: provider.is_active || true
      });
    }
  }, [provider]);

  // Update form errors when props change
  useEffect(() => {
    setFormErrors(errors);
  }, [errors]);

  const businessTypes = [
    'Individual',
    'Partnership',
    'Corporation',
    'LLC',
    'Other'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setIsSubmitting(true);

    // Client-side validation
    const validationErrors: Record<string, string> = {};
    
    if (!formData.business_name.trim()) {
      validationErrors.business_name = 'Business name is required';
    }

    if (!formData.business_type) {
      validationErrors.business_type = 'Business type is required';
    }

    if (!formData.business_email.trim()) {
      validationErrors.business_email = 'Business email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.business_email)) {
      validationErrors.business_email = 'Please enter a valid email address';
    }

    if (!formData.contact_person.trim()) {
      validationErrors.contact_person = 'Contact person is required';
    }

    if (!formData.contact_phone.trim()) {
      validationErrors.contact_phone = 'Contact phone is required';
    }

    if (!formData.city.trim()) {
      validationErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      validationErrors.state = 'State is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = {
        business_name: formData.business_name.trim(),
        business_type: formData.business_type,
        business_email: formData.business_email.trim(),
        contact_person: formData.contact_person.trim(),
        contact_phone: formData.contact_phone.trim(),
        contact_email: formData.contact_email.trim(),
        description: formData.description?.trim(),
        website: formData.website?.trim(),
        address: formData.address?.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        years_experience: Number(formData.years_experience),
        is_verified: formData.is_verified,
        is_active: formData.is_active
      };

      await onSubmit(submitData);
    } catch (error: any) {
      setFormErrors(error.errors || { general: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ProviderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-first sticky header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onCancel}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                disabled={isSubmitting}
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Back to Providers</span>
              </button>
              <div className="ml-4 sm:ml-8">
                <h1 className="text-xl font-semibold text-gray-900">
                  {provider ? 'Edit Provider' : 'Create Provider'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-6">
                {/* General errors */}
                {formErrors.general && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{formErrors.general}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Information Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Business Information
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Business Name */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={formData.business_name}
                        onChange={(e) => handleInputChange('business_name', e.target.value)}
                        placeholder="Enter business name"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.business_name ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.business_name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.business_name}</p>
                      )}
                    </div>

                    {/* Business Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type *
                      </label>
                      <select
                        value={formData.business_type}
                        onChange={(e) => handleInputChange('business_type', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.business_type ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      >
                        <option value="">Select business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {formErrors.business_type && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.business_type}</p>
                      )}
                    </div>

                    {/* Years of Experience */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={formData.years_experience}
                        onChange={(e) => handleInputChange('years_experience', Number(e.target.value))}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.years_experience ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.years_experience && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.years_experience}</p>
                      )}
                    </div>

                    {/* Business Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Email *
                      </label>
                      <input
                        type="email"
                        value={formData.business_email}
                        onChange={(e) => handleInputChange('business_email', e.target.value)}
                        placeholder="business@example.com"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.business_email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.business_email && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.business_email}</p>
                      )}
                    </div>

                    {/* Website */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        placeholder="https://www.example.com"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.website ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.website && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.website}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Describe your business and services"
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.description ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.description && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Contact Information
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Contact Person */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person *
                      </label>
                      <input
                        type="text"
                        value={formData.contact_person}
                        onChange={(e) => handleInputChange('contact_person', e.target.value)}
                        placeholder="Enter contact person name"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.contact_person ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.contact_person && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.contact_person}</p>
                      )}
                    </div>

                    {/* Contact Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone *
                      </label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.contact_phone ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.contact_phone && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.contact_phone}</p>
                      )}
                    </div>

                    {/* Contact Email */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleInputChange('contact_email', e.target.value)}
                        placeholder="contact@example.com"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.contact_email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.contact_email && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.contact_email}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location Information Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <MapPin className="h-5 w-5 mr-2" />
                    Location Information
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Address */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Enter business address"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.address ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.address && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                      )}
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Enter city"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.city ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.city && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>
                      )}
                    </div>

                    {/* State */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="Enter state"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.state ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.state && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.state}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Settings - Only for admin editing existing providers */}
                {provider && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Status Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.is_verified}
                            onChange={(e) => handleInputChange('is_verified', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={isSubmitting}
                          />
                          <span className="ml-2 text-sm text-gray-700">Verified Provider</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                          Verified providers have completed the verification process
                        </p>
                      </div>

                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={isSubmitting}
                          />
                          <span className="ml-2 text-sm text-gray-700">Active Provider</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                          Active providers can receive new bookings and appear in search results
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Form actions */}
            <div className="px-4 py-3 sm:px-6 bg-gray-50 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {provider ? 'Update' : 'Create'} Provider
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProviderForm;